import { Button, Checkbox, Flex, InputText, Select } from '@driftt/dds'
import * as R from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { fetchWorkspacesCounts } from 'api/workspaces'
import {
  PLAYBOOK_EDITOR_TYPES_STATUS_OPTIONS,
  TABLE_STATES,
  WORKSPACE_SUPPORTED_PLAYBOOK_TYPES,
} from 'components/PlaybooksTable/constants'
import useDebounce from 'hooks/useDebounce'
import { getOrgWorkspaces } from 'modules/workspace/selectors'
import {
  getWorkspacesWithReadablePlaybooks,
  getWorkspacesWithWritablePlaybooks,
  orgHasPlaybookPriorityFeature,
  userHasPlaybookPriorityAccess,
} from 'utils/access'
import { isEmptyOrNil } from 'utils/index'
import {
  filterDefaultWorkspaceIfEmpty,
  getDefaultPlaybookCounts,
} from 'utils/workspaces'

export default function PlaybooksTableHeader({
  playbookType = 'CHAT',
  onChange,
  openPriorityPageOrModal = (playbookType) => {},
}) {
  const [playbookStatus, setPlaybookStatus] = useState(
    PLAYBOOK_EDITOR_TYPES_STATUS_OPTIONS[playbookType][0]
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [showDisabledPlaybooks, setShowDisabledPlaybooks] = useState(true)
  const [playbooksInDefault, setPlaybooksInDefault] = useState(1)

  const orgWorkspaces = useSelector(getOrgWorkspaces)
  const readableWorkspaceIds = getWorkspacesWithReadablePlaybooks({
    playbookType,
  })
  const filterableWorkspaces = filterDefaultWorkspaceIfEmpty({
    workspaceIds: readableWorkspaceIds,
    playbooksInDefault,
  }).map((id) => ({
    label: orgWorkspaces?.[id]?.name,
    value: orgWorkspaces?.[id]?.id,
  }))

  const showWorkspaces =
    readableWorkspaceIds.length > 1 &&
    WORKSPACE_SUPPORTED_PLAYBOOK_TYPES.includes(playbookType)

  const [selectedWorkspaces, setSelectedWorkspaces] = useState([])

  const debouncedSearchTerm = useDebounce(searchTerm)
  const showCheckbox = useMemo(
    () => playbookStatus.value.includes('_ACTIVE'),
    [playbookStatus]
  )
  const statusTypeOptions = useMemo(
    () => PLAYBOOK_EDITOR_TYPES_STATUS_OPTIONS[playbookType],
    [playbookType]
  )

  const writableWorkspaceIds = getWorkspacesWithWritablePlaybooks({
    playbookType,
  })
  const hasWritableWorkspaces = !!writableWorkspaceIds.length

  useEffect(() => {
    onChange({ nameQuery: debouncedSearchTerm, page: 0 })
  }, [debouncedSearchTerm, onChange])

  useEffect(() => {
    setPlaybookStatus(PLAYBOOK_EDITOR_TYPES_STATUS_OPTIONS[playbookType][0])
  }, [playbookType])

  useEffect(() => {
    const fetchCounts = async () => {
      fetchWorkspacesCounts()
        .then(({ data }) =>
          setPlaybooksInDefault(getDefaultPlaybookCounts(data))
        )
        .catch((err) => console.error(err))
    }
    showWorkspaces && fetchCounts()
  }, [showWorkspaces])

  function handleOnSearchChange(e) {
    setSearchTerm(e.target.value)
  }

  function handleOnStatusChange(option) {
    const newOption = isEmptyOrNil(option) ? statusTypeOptions[0] : option
    setPlaybookStatus(newOption)

    onChange({
      category: newOption.value,
      filterEnabled: false,
      page: 0,
      sortDirection: newOption.sortProperties.dir,
      sortProperty: newOption.sortProperties.property,
    })
  }

  function handleToggleDisabledPlaybooks(isChecked) {
    setShowDisabledPlaybooks(isChecked)
    onChange({ filterEnabled: !isChecked, page: 0 })
  }

  function handleWorkspaceChange(selection) {
    setSelectedWorkspaces(selection)
    onChange({ workspaceId: R.pluck('value', selection), page: 0 })
  }

  const showPriorityButton = useMemo(() => {
    if (orgHasPlaybookPriorityFeature()) {
      return userHasPlaybookPriorityAccess()
    } else {
      return hasWritableWorkspaces
    }
  }, [hasWritableWorkspaces])

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      fullWidth
      css={{ backgroundColor: '$white', padding: '$2' }}
      wrap="wrap"
    >
      <Flex css={{ minWidth: '180px', maxWidth: '280px', flex: 1 }}>
        <InputText
          onChange={handleOnSearchChange}
          placeholder="Search by name or ID"
          value={searchTerm}
          size="full-width"
        />
      </Flex>
      <Flex alignItems="center" wrap="wrap">
        {showCheckbox && (
          <Checkbox
            checked={showDisabledPlaybooks}
            label="Show disabled playbooks"
            onChange={handleToggleDisabledPlaybooks}
            type="checkbox"
          />
        )}
        {showWorkspaces && (
          <Select
            options={filterableWorkspaces}
            placeholder="All workspaces"
            onChange={handleWorkspaceChange}
            value={selectedWorkspaces}
            multi
          />
        )}
        <Select
          onChange={handleOnStatusChange}
          options={statusTypeOptions}
          value={playbookStatus}
        />
        {showPriorityButton && (
          <Button
            disabled={
              ![
                TABLE_STATES.CHAT_ACTIVE,
                TABLE_STATES.MEETING_BOOKING_ACTIVE,
              ].some((state) => state === playbookStatus.value)
            }
            onClick={() => openPriorityPageOrModal(playbookType)}
            variant="secondary"
          >
            Set priority
          </Button>
        )}
      </Flex>
    </Flex>
  )
}
